import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Send, MessageCircle, X, Search,
  Users, Plus, ArrowLeft, UserPlus, Check, Hash, ImagePlus, XCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

interface Connection {
  connectionId: string;
  userId: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  unreadCount: number;
  lastMessage: string | null;
  lastMessageAt: string | null;
}

interface DirectMessage {
  id: string;
  connectionId: string;
  senderId: string;
  content: string;
  imageUrl: string | null;
  read: boolean;
  createdAt: string;
}

interface GroupChat {
  id: string;
  name: string;
  createdBy: string;
  updatedAt: string;
  memberCount: number;
  lastMessage: string | null;
  lastMessageAt: string | null;
}

interface GroupMessage {
  id: string;
  groupId: string;
  senderId: string;
  senderName: string;
  content: string;
  imageUrl: string | null;
  createdAt: string;
}

interface GroupMember {
  userId: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
}

type ActiveTab = 'dms' | 'groups';
type ChatView = 'list' | 'dm' | 'group' | 'new_group';

interface Props {
  theme: string;
}

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function formatMessageTime(ts: string): string {
  const date = new Date(ts);
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  const dateStr = sameYear
    ? date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${dateStr} ${timeStr}`;
}

function Avatar({
  photoUrl, firstName, lastName, size = 10
}: { photoUrl: string | null; firstName: string; lastName: string; size?: number }) {
  const [imgError, setImgError] = useState(false);

  if (photoUrl && !imgError) {
    return (
      <img
        src={photoUrl}
        alt={`${firstName} ${lastName}`}
        className={`w-${size} h-${size} rounded-full object-cover flex-shrink-0`}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div
      className={`w-${size} h-${size} rounded-full bg-[#40E0D0] flex items-center justify-center text-white font-semibold flex-shrink-0`}
      style={{ fontSize: size <= 8 ? '0.7rem' : '0.85rem' }}
    >
      {firstName.charAt(0)}{lastName.charAt(0)}
    </div>
  );
}

export default function ConnectionsChat({ theme }: Props) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('dms');
  const [view, setView] = useState<ChatView>('list');

  // DM state
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedDm, setSelectedDm] = useState<Connection | null>(null);
  const [dmMessages, setDmMessages] = useState<DirectMessage[]>([]);

  // Group state
  const [groups, setGroups] = useState<GroupChat[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GroupChat | null>(null);
  const [groupMessages, setGroupMessages] = useState<GroupMessage[]>([]);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);

  // New group creation
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedMembersForGroup, setSelectedMembersForGroup] = useState<string[]>([]);
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Add member to existing group
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [addMemberError, setAddMemberError] = useState<string | null>(null);
  const [addingMemberId, setAddingMemberId] = useState<string | null>(null);

  // Common
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dmChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const groupChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const isDark = theme === 'dark';
  const bg = isDark ? 'bg-gray-900' : 'bg-white';
  const border = isDark ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const hoverBg = isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50';
  const headerBg = isDark ? 'bg-gray-800' : 'bg-gray-50';
  const inputBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';
  const selectedBg = isDark ? 'bg-gray-700' : 'bg-[#e6faf9]';

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const fetchConnections = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('community_connections')
      .select('id, requester_id, addressee_id, status')
      .eq('status', 'accepted')
      .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`);

    if (error || !data) return;

    const peerIds = data.map((c) => ({
      connectionId: c.id,
      peerId: c.requester_id === user.id ? c.addressee_id : c.requester_id,
    }));

    if (peerIds.length === 0) { setConnections([]); return; }

    const peerUids = peerIds.map((p) => p.peerId);
    const { data: profiles } = await supabase
      .from('consultant_profiles')
      .select('id, first_name, last_name, photo_url')
      .in('id', peerUids);

    const profileMap: Record<string, { first_name: string; last_name: string; photo_url: string | null }> = {};
    (profiles || []).forEach((p) => { profileMap[p.id] = p; });

    const connIds = data.map((c) => c.id);
    const { data: msgData } = await supabase
      .from('connection_direct_messages')
      .select('connection_id, content, created_at, sender_id, read')
      .in('connection_id', connIds)
      .order('created_at', { ascending: false });

    const lastMsgMap: Record<string, { content: string; at: string }> = {};
    const unreadMap: Record<string, number> = {};
    (msgData || []).forEach((m) => {
      if (!lastMsgMap[m.connection_id]) {
        lastMsgMap[m.connection_id] = { content: m.content, at: m.created_at };
      }
      if (!m.read && m.sender_id !== user.id) {
        unreadMap[m.connection_id] = (unreadMap[m.connection_id] || 0) + 1;
      }
    });

    const list: Connection[] = peerIds.map(({ connectionId, peerId }) => {
      const profile = profileMap[peerId];
      return {
        connectionId,
        userId: peerId,
        firstName: profile?.first_name || 'Unknown',
        lastName: profile?.last_name || '',
        photoUrl: profile?.photo_url || null,
        unreadCount: unreadMap[connectionId] || 0,
        lastMessage: lastMsgMap[connectionId]?.content || null,
        lastMessageAt: lastMsgMap[connectionId]?.at || null,
      };
    });

    list.sort((a, b) => {
      if (a.lastMessageAt && b.lastMessageAt)
        return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
      return a.lastMessageAt ? -1 : 1;
    });

    setConnections(list);
  }, [user]);

  const fetchGroups = useCallback(async () => {
    if (!user) return;

    const { data: memberRows } = await supabase
      .from('group_chat_members')
      .select('group_id')
      .eq('user_id', user.id);

    if (!memberRows || memberRows.length === 0) { setGroups([]); return; }

    const groupIds = memberRows.map((r) => r.group_id);

    const { data: groupData } = await supabase
      .from('group_chats')
      .select('id, name, created_by, updated_at')
      .in('id', groupIds)
      .order('updated_at', { ascending: false });

    if (!groupData) return;

    const { data: allMembers } = await supabase
      .from('group_chat_members')
      .select('group_id')
      .in('group_id', groupIds);

    const memberCountMap: Record<string, number> = {};
    (allMembers || []).forEach((m) => {
      memberCountMap[m.group_id] = (memberCountMap[m.group_id] || 0) + 1;
    });

    const { data: lastMsgs } = await supabase
      .from('group_chat_messages')
      .select('group_id, content, created_at')
      .in('group_id', groupIds)
      .order('created_at', { ascending: false });

    const lastMsgMap: Record<string, { content: string; at: string }> = {};
    (lastMsgs || []).forEach((m) => {
      if (!lastMsgMap[m.group_id])
        lastMsgMap[m.group_id] = { content: m.content, at: m.created_at };
    });

    setGroups(groupData.map((g) => ({
      id: g.id,
      name: g.name,
      createdBy: g.created_by,
      updatedAt: g.updated_at,
      memberCount: memberCountMap[g.id] || 0,
      lastMessage: lastMsgMap[g.id]?.content || null,
      lastMessageAt: lastMsgMap[g.id]?.at || null,
    })));
  }, [user]);

  const fetchDmMessages = useCallback(async (connectionId: string) => {
    setLoading(true);
    const { data, error } = await supabase
      .from('connection_direct_messages')
      .select('*')
      .eq('connection_id', connectionId)
      .order('created_at', { ascending: true });

    if (!error && data) {
      setDmMessages(data.map((m) => ({
        id: m.id,
        connectionId: m.connection_id,
        senderId: m.sender_id,
        content: m.content,
        imageUrl: m.image_url || null,
        read: m.read,
        createdAt: m.created_at,
      })));

      await supabase
        .from('connection_direct_messages')
        .update({ read: true })
        .eq('connection_id', connectionId)
        .neq('sender_id', user!.id)
        .eq('read', false);

      setConnections((prev) =>
        prev.map((c) => c.connectionId === connectionId ? { ...c, unreadCount: 0 } : c)
      );
    }
    setLoading(false);
  }, [user]);

  const fetchGroupMessages = useCallback(async (groupId: string) => {
    setLoading(true);
    const { data: msgs } = await supabase
      .from('group_chat_messages')
      .select('id, group_id, sender_id, content, image_url, created_at')
      .eq('group_id', groupId)
      .order('created_at', { ascending: true });

    const { data: members } = await supabase
      .from('group_chat_members')
      .select('user_id')
      .eq('group_id', groupId);

    const memberIds = (members || []).map((m) => m.user_id);

    const { data: profiles } = await supabase
      .from('consultant_profiles')
      .select('id, first_name, last_name, photo_url')
      .in('id', memberIds);

    const profileMap: Record<string, { first_name: string; last_name: string; photo_url: string | null }> = {};
    (profiles || []).forEach((p) => { profileMap[p.id] = p; });

    setGroupMembers(
      (members || []).map((m) => {
        const p = profileMap[m.user_id];
        return {
          userId: m.user_id,
          firstName: p?.first_name || 'Unknown',
          lastName: p?.last_name || '',
          photoUrl: p?.photo_url || null,
        };
      })
    );

    setGroupMessages(
      (msgs || []).map((m) => {
        const p = profileMap[m.sender_id];
        return {
          id: m.id,
          groupId: m.group_id,
          senderId: m.sender_id,
          senderName: p ? `${p.first_name} ${p.last_name}` : 'Unknown',
          content: m.content,
          imageUrl: m.image_url || null,
          createdAt: m.created_at,
        };
      })
    );
    setLoading(false);
  }, []);

  // ─── Subscriptions ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (open) {
      fetchConnections();
      fetchGroups();
    }
  }, [open, fetchConnections, fetchGroups]);

  // Auto-open a group chat when navigated here with ?group=<id>
  useEffect(() => {
    const groupId = searchParams.get('group');
    if (!groupId || !user) return;

    const openGroupById = async () => {
      // Fetch the group data directly
      const { data: groupData } = await supabase
        .from('group_chats')
        .select('id, name, created_by, updated_at')
        .eq('id', groupId)
        .maybeSingle();

      if (!groupData) return;

      const { data: memberRows } = await supabase
        .from('group_chat_members')
        .select('group_id')
        .eq('group_id', groupId);

      const group: GroupChat = {
        id: groupData.id,
        name: groupData.name,
        createdBy: groupData.created_by,
        updatedAt: groupData.updated_at,
        memberCount: memberRows?.length || 0,
        lastMessage: null,
        lastMessageAt: null,
      };

      setOpen(true);
      setActiveTab('groups');
      setSelectedGroup(group);
      setView('group');
      // Clear the param so navigating back doesn't re-trigger
      setSearchParams((prev) => { prev.delete('group'); return prev; }, { replace: true });
    };

    openGroupById();
  }, [searchParams, user]);

  useEffect(() => {
    if (!selectedDm) return;
    fetchDmMessages(selectedDm.connectionId);

    if (dmChannelRef.current) supabase.removeChannel(dmChannelRef.current);

    dmChannelRef.current = supabase
      .channel(`cdm_${selectedDm.connectionId}_${user?.id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'connection_direct_messages',
        filter: `connection_id=eq.${selectedDm.connectionId}`,
      }, async (payload) => {
        const m = payload.new as Record<string, unknown>;
        const newMsg: DirectMessage = {
          id: m.id as string,
          connectionId: m.connection_id as string,
          senderId: m.sender_id as string,
          content: (m.content as string) || '',
          imageUrl: (m.image_url as string) || null,
          read: m.read as boolean,
          createdAt: m.created_at as string,
        };
        setDmMessages((prev) => {
          if (prev.some((msg) => msg.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
        if (newMsg.senderId !== user?.id) {
          await supabase.from('connection_direct_messages').update({ read: true }).eq('id', newMsg.id);
        }
      })
      .subscribe();

    return () => { if (dmChannelRef.current) { supabase.removeChannel(dmChannelRef.current); dmChannelRef.current = null; } };
  }, [selectedDm, fetchDmMessages, user]);

  useEffect(() => {
    if (!selectedGroup) return;
    fetchGroupMessages(selectedGroup.id);

    if (groupChannelRef.current) supabase.removeChannel(groupChannelRef.current);

    groupChannelRef.current = supabase
      .channel(`gcm_${selectedGroup.id}_${user?.id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'group_chat_messages',
        filter: `group_id=eq.${selectedGroup.id}`,
      }, async (payload) => {
        const m = payload.new as Record<string, unknown>;
        const { data: profile } = await supabase
          .from('consultant_profiles')
          .select('first_name, last_name')
          .eq('id', m.sender_id as string)
          .maybeSingle();

        const newGroupMsg = {
          id: m.id as string,
          groupId: m.group_id as string,
          senderId: m.sender_id as string,
          senderName: profile ? `${profile.first_name} ${profile.last_name}` : 'Unknown',
          content: (m.content as string) || '',
          imageUrl: (m.image_url as string) || null,
          createdAt: m.created_at as string,
        };
        setGroupMessages((prev) => {
          if (prev.some((msg) => msg.id === newGroupMsg.id)) return prev;
          return [...prev, newGroupMsg];
        });
      })
      .subscribe();

    return () => { if (groupChannelRef.current) { supabase.removeChannel(groupChannelRef.current); groupChannelRef.current = null; } };
  }, [selectedGroup, fetchGroupMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [dmMessages, groupMessages]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${user!.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('chat-images').upload(path, file, { upsert: true, contentType: file.type });
    if (error) {
      console.error('Image upload error:', error);
      return null;
    }
    const { data } = supabase.storage.from('chat-images').getPublicUrl(path);
    return data.publicUrl;
  };

  const sendDm = async () => {
    if ((!draft.trim() && !imageFile) || !selectedDm || !user || sending) return;
    const content = draft.trim();
    setDraft('');
    setSending(true);

    let imageUrl: string | null = null;
    if (imageFile) {
      setUploadingImage(true);
      imageUrl = await uploadImage(imageFile);
      setUploadingImage(false);
      clearImage();
    }

    const { data: inserted, error: insertErr } = await supabase
      .from('connection_direct_messages')
      .insert({
        connection_id: selectedDm.connectionId,
        sender_id: user.id,
        content: content || null,
        image_url: imageUrl,
      })
      .select()
      .single();

    if (insertErr) {
      console.error('DM insert error:', insertErr);
    } else if (inserted) {
      const newMsg: DirectMessage = {
        id: inserted.id,
        connectionId: inserted.connection_id,
        senderId: inserted.sender_id,
        content: inserted.content || '',
        imageUrl: inserted.image_url || null,
        read: inserted.read,
        createdAt: inserted.created_at,
      };
      setDmMessages((prev) => {
        if (prev.some((m) => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
    }

    setConnections((prev) =>
      prev.map((c) => c.connectionId === selectedDm.connectionId
        ? { ...c, lastMessage: content || 'Image', lastMessageAt: new Date().toISOString() }
        : c
      )
    );
    setSending(false);
    inputRef.current?.focus();
  };

  const sendGroupMessage = async () => {
    if ((!draft.trim() && !imageFile) || !selectedGroup || !user || sending) return;
    const content = draft.trim();
    setDraft('');
    setSending(true);

    let imageUrl: string | null = null;
    if (imageFile) {
      setUploadingImage(true);
      imageUrl = await uploadImage(imageFile);
      setUploadingImage(false);
      clearImage();
    }

    const { data: inserted, error: insertErr } = await supabase
      .from('group_chat_messages')
      .insert({
        group_id: selectedGroup.id,
        sender_id: user.id,
        content: content || null,
        image_url: imageUrl,
      })
      .select()
      .single();

    if (insertErr) {
      console.error('Group msg insert error:', insertErr);
    } else if (inserted) {
      const myProfile = groupMembers.find((m) => m.userId === user.id);
      const senderName = myProfile
        ? `${myProfile.firstName} ${myProfile.lastName}`
        : 'You';
      const newMsg: GroupMessage = {
        id: inserted.id,
        groupId: inserted.group_id,
        senderId: inserted.sender_id,
        senderName,
        content: inserted.content || '',
        imageUrl: inserted.image_url || null,
        createdAt: inserted.created_at,
      };
      setGroupMessages((prev) => {
        if (prev.some((m) => m.id === newMsg.id)) return prev;
        return [...prev, newMsg];
      });
    }

    await supabase.from('group_chats').update({ updated_at: new Date().toISOString() }).eq('id', selectedGroup.id);
    setGroups((prev) =>
      prev.map((g) => g.id === selectedGroup.id
        ? { ...g, lastMessage: content || 'Image', lastMessageAt: new Date().toISOString() }
        : g
      )
    );
    setSending(false);
    inputRef.current?.focus();
  };

  const createGroup = async () => {
    if (!newGroupName.trim() || selectedMembersForGroup.length === 0 || !user || creatingGroup) return;
    setCreatingGroup(true);

    const groupName = newGroupName.trim();
    const membersToAdd = [...selectedMembersForGroup];

    // Step 1: Create the group
    const { data: groupData, error: groupError } = await supabase
      .from('group_chats')
      .insert({ name: groupName, created_by: user.id })
      .select('id, name, created_at, updated_at')
      .single();

    if (groupError || !groupData) {
      console.error('Failed to create group:', groupError);
      setCreatingGroup(false);
      return;
    }

    // Step 2: Add creator as member first
    const { error: creatorErr } = await supabase
      .from('group_chat_members')
      .insert({ group_id: groupData.id, user_id: user.id });

    if (creatorErr) {
      console.error('Failed to add creator as member:', creatorErr);
      setCreatingGroup(false);
      return;
    }

    // Step 3: Add other members one at a time to avoid batch trigger failures
    for (const uid of membersToAdd) {
      const { error: memberErr } = await supabase
        .from('group_chat_members')
        .insert({ group_id: groupData.id, user_id: uid });
      if (memberErr) {
        console.error('Failed to add member', uid, memberErr);
      }
    }

    // Step 4: Reset form state
    setNewGroupName('');
    setSelectedMembersForGroup([]);
    setCreatingGroup(false);

    // Step 5: Navigate to the new group — the useEffect on selectedGroup will
    // call fetchGroupMessages and set up the real-time subscription
    const newGroup: GroupChat = {
      id: groupData.id,
      name: groupData.name,
      createdBy: user.id,
      updatedAt: groupData.updated_at,
      memberCount: 1 + membersToAdd.length,
      lastMessage: null,
      lastMessageAt: null,
    };

    setGroups((prev) => [newGroup, ...prev]);
    setSelectedGroup(newGroup);
    setView('group');

    // Refresh groups list in background so member counts are accurate
    fetchGroups();
  };

  const addMemberToGroup = async (userId: string) => {
    if (!selectedGroup || addingMemberId) return;
    setAddingMemberId(userId);
    setAddMemberError(null);

    const { error } = await supabase
      .from('group_chat_members')
      .insert({ group_id: selectedGroup.id, user_id: userId });

    if (error) {
      console.error('Failed to add member:', error);
      setAddMemberError('Could not add member. Please try again.');
      setAddingMemberId(null);
      return;
    }

    // Refresh members list so the new member appears and nonMembers updates
    await fetchGroupMessages(selectedGroup.id);
    setGroups((prev) =>
      prev.map((g) => g.id === selectedGroup.id ? { ...g, memberCount: g.memberCount + 1 } : g)
    );
    setAddingMemberId(null);
    setShowAddMember(false);
    setAddMemberSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (view === 'dm') sendDm();
      else if (view === 'group') sendGroupMessage();
    }
  };

  const canSend = (draft.trim().length > 0 || imageFile !== null) && !sending && !uploadingImage;

  const openDm = (conn: Connection) => {
    setSelectedDm(conn);
    setDraft('');
    setView('dm');
  };

  const openGroup = (group: GroupChat) => {
    setSelectedGroup(group);
    setDraft('');
    setView('group');
  };

  const goBack = () => {
    setView('list');
    setSelectedDm(null);
    setSelectedGroup(null);
    setDmMessages([]);
    setGroupMessages([]);
    setGroupMembers([]);
    setShowAddMember(false);
  };

  const totalDmUnread = connections.reduce((s, c) => s + c.unreadCount, 0);

  // Members not yet in the group (for adding)
  const nonMembers = connections.filter(
    (c) => !groupMembers.some((m) => m.userId === c.userId)
  ).filter((c) =>
    addMemberSearch === '' ||
    `${c.firstName} ${c.lastName}`.toLowerCase().includes(addMemberSearch.toLowerCase())
  );

  const filteredConnections = connections.filter((c) =>
    search === '' || `${c.firstName} ${c.lastName}`.toLowerCase().includes(search.toLowerCase())
  );

  const filteredGroups = groups.filter((g) =>
    search === '' || g.name.toLowerCase().includes(search.toLowerCase())
  );

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const renderDmList = () => (
    <div className="flex-1 overflow-y-auto">
      {filteredConnections.length === 0 ? (
        <div className={`flex flex-col items-center justify-center h-full gap-3 px-6 text-center ${textSecondary}`}>
          <MessageCircle className="w-10 h-10 opacity-30" />
          <p className="text-sm">
            {connections.length === 0
              ? 'No connections yet. Connect with others in the community!'
              : 'No matches found.'}
          </p>
        </div>
      ) : (
        filteredConnections.map((conn) => (
          <button
            key={conn.connectionId}
            onClick={() => openDm(conn)}
            className={`w-full flex items-center gap-3 px-4 py-3 border-b transition-colors text-left ${border} ${hoverBg}`}
          >
            <div className="relative flex-shrink-0">
              <Avatar photoUrl={conn.photoUrl} firstName={conn.firstName} lastName={conn.lastName} size={10} />
              {conn.unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#40E0D0] text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {conn.unreadCount > 9 ? '9+' : conn.unreadCount}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium truncate ${textPrimary}`}>
                  {conn.firstName} {conn.lastName}
                </span>
                {conn.lastMessageAt && (
                  <span className={`text-xs flex-shrink-0 ml-2 ${textSecondary}`}>
                    {timeAgo(conn.lastMessageAt)}
                  </span>
                )}
              </div>
              {conn.lastMessage && (
                <p className={`text-xs truncate mt-0.5 ${conn.unreadCount > 0 ? `font-medium ${textPrimary}` : textSecondary}`}>
                  {conn.lastMessage}
                </p>
              )}
            </div>
          </button>
        ))
      )}
    </div>
  );

  const renderGroupList = () => (
    <div className="flex-1 overflow-y-auto">
      <button
        onClick={() => { setView('new_group'); setSearch(''); }}
        className={`w-full flex items-center gap-3 px-4 py-3 border-b transition-colors ${border} ${hoverBg} text-[#40E0D0]`}
      >
        <div className="w-10 h-10 rounded-full border-2 border-dashed border-[#40E0D0] flex items-center justify-center flex-shrink-0">
          <Plus className="w-4 h-4" />
        </div>
        <span className="text-sm font-medium">Create new group</span>
      </button>

      {filteredGroups.length === 0 ? (
        <div className={`flex flex-col items-center justify-center py-12 gap-3 px-6 text-center ${textSecondary}`}>
          <Users className="w-10 h-10 opacity-30" />
          <p className="text-sm">No group chats yet. Create one above!</p>
        </div>
      ) : (
        filteredGroups.map((group) => (
          <button
            key={group.id}
            onClick={() => openGroup(group)}
            className={`w-full flex items-center gap-3 px-4 py-3 border-b transition-colors text-left ${border} ${hoverBg}`}
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#40E0D0] to-[#2ab5a8] flex items-center justify-center flex-shrink-0">
              <Hash className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium truncate ${textPrimary}`}>{group.name}</span>
                {group.lastMessageAt && (
                  <span className={`text-xs flex-shrink-0 ml-2 ${textSecondary}`}>
                    {timeAgo(group.lastMessageAt)}
                  </span>
                )}
              </div>
              <p className={`text-xs mt-0.5 ${textSecondary}`}>
                {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                {group.lastMessage && ` · ${group.lastMessage.substring(0, 30)}${group.lastMessage.length > 30 ? '…' : ''}`}
              </p>
            </div>
          </button>
        ))
      )}
    </div>
  );

  const renderNewGroup = () => {
    const selectedConnections = connections.filter((c) => selectedMembersForGroup.includes(c.userId));
    const canCreate = newGroupName.trim().length > 0 && selectedMembersForGroup.length > 0 && !creatingGroup;

    const toggleMember = (conn: Connection) => {
      const isSelected = selectedMembersForGroup.includes(conn.userId);
      const next = isSelected
        ? selectedMembersForGroup.filter((id) => id !== conn.userId)
        : [...selectedMembersForGroup, conn.userId];

      setSelectedMembersForGroup(next);

      // Auto-suggest group name if user hasn't customised it yet
      if (!newGroupName.trim() || connections.some((c) =>
        newGroupName === c.firstName + ' & Group' ||
        newGroupName.startsWith('Group with ')
      )) {
        const nextConns = connections.filter((c) => next.includes(c.userId));
        if (nextConns.length === 0) {
          setNewGroupName('');
        } else if (nextConns.length === 1) {
          setNewGroupName(`Group with ${nextConns[0].firstName}`);
        } else {
          setNewGroupName(`${nextConns[0].firstName}, ${nextConns[1].firstName}${nextConns.length > 2 ? ` +${nextConns.length - 2}` : ''} Group`);
        }
      }
    };

    return (
      <div className="flex flex-col h-full">
        <div className={`flex items-center gap-3 px-4 py-3 border-b ${border} ${headerBg}`}>
          <button onClick={() => setView('list')} className={`p-1 rounded-md transition-colors ${hoverBg} ${textSecondary}`}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className={`font-semibold text-sm ${textPrimary}`}>New Group Chat</span>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Step 1: Pick members */}
          <div>
            <label className={`block text-xs font-medium mb-2 ${textSecondary}`}>
              Select Members <span className="text-[#40E0D0]">({selectedMembersForGroup.length} selected)</span>
            </label>
            {connections.length === 0 ? (
              <p className={`text-xs ${textSecondary}`}>No connections to add. Connect with others first.</p>
            ) : (
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {connections.map((conn) => {
                  const isSelected = selectedMembersForGroup.includes(conn.userId);
                  return (
                    <button
                      key={conn.userId}
                      onClick={() => toggleMember(conn)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left border ${
                        isSelected
                          ? `${selectedBg} border-[#40E0D0]/40`
                          : `border-transparent ${hoverBg}`
                      }`}
                    >
                      <Avatar photoUrl={conn.photoUrl} firstName={conn.firstName} lastName={conn.lastName} size={8} />
                      <span className={`flex-1 text-sm truncate ${textPrimary}`}>
                        {conn.firstName} {conn.lastName}
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? 'bg-[#40E0D0] border-[#40E0D0]' : isDark ? 'border-gray-600' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step 2: Name the group */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${textSecondary}`}>
              Group Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder={selectedConnections.length > 0 ? 'Auto-filled — edit if you like' : 'Select members first…'}
              className={`w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputBg} ${textPrimary} ${
                !newGroupName.trim() && selectedMembersForGroup.length > 0 ? 'border-red-400' : ''
              }`}
              maxLength={100}
            />
            {!newGroupName.trim() && selectedMembersForGroup.length > 0 && (
              <p className="text-xs text-red-400 mt-1">Please enter a group name to continue.</p>
            )}
          </div>
        </div>
        <div className={`p-3 border-t ${border}`}>
          <button
            onClick={createGroup}
            disabled={!canCreate}
            className="w-full py-2 rounded-lg bg-[#40E0D0] hover:bg-[#35c5b5] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creatingGroup ? 'Creating…' : selectedMembersForGroup.length === 0 ? 'Select members to continue' : !newGroupName.trim() ? 'Enter a group name' : 'Create Group'}
          </button>
        </div>
      </div>
    );
  };

  const renderDmChat = () => {
    if (!selectedDm) return null;
    return (
      <div className="flex flex-col h-full">
        <div className={`flex items-center gap-3 px-4 py-3 border-b ${border} ${headerBg}`}>
          <button onClick={goBack} className={`p-1 rounded-md transition-colors ${hoverBg} ${textSecondary}`}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Avatar photoUrl={selectedDm.photoUrl} firstName={selectedDm.firstName} lastName={selectedDm.lastName} size={8} />
          <span className={`flex-1 text-sm font-semibold truncate ${textPrimary}`}>
            {selectedDm.firstName} {selectedDm.lastName}
          </span>
          <button onClick={() => setOpen(false)} className={`p-1 rounded-md transition-colors ${hoverBg} ${textSecondary}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <div className="flex justify-center pt-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#40E0D0]"></div>
            </div>
          ) : dmMessages.length === 0 ? (
            <div className={`flex flex-col items-center justify-center h-full gap-2 text-center ${textSecondary}`}>
              <MessageCircle className="w-8 h-8 opacity-30" />
              <p className="text-xs">No messages yet. Say hello!</p>
            </div>
          ) : (
            dmMessages.map((msg, idx) => {
              const isMine = msg.senderId === user?.id;
              const prevMsg = idx > 0 ? dmMessages[idx - 1] : null;
              const isNewBlock = !prevMsg || prevMsg.senderId !== msg.senderId;
              const senderLabel = isMine ? 'You' : `${selectedDm.firstName} ${selectedDm.lastName}`;
              return (
                <div key={msg.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                  {isNewBlock && (
                    <p className={`text-xs font-semibold mb-1 ${isMine ? 'mr-1 text-right' : 'ml-1 text-left'} ${textSecondary}`}>
                      {senderLabel}
                    </p>
                  )}
                  <div className="max-w-[78%]">
                    {msg.imageUrl && (
                      <div className={`mb-1 rounded-2xl overflow-hidden ${isMine ? 'rounded-br-sm' : 'rounded-bl-sm'}`}>
                        <img
                          src={msg.imageUrl}
                          alt="shared image"
                          className="max-w-full max-h-48 object-contain w-full cursor-pointer"
                          onClick={() => window.open(msg.imageUrl!, '_blank')}
                        />
                      </div>
                    )}
                    {msg.content && (
                      <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                        isMine
                          ? 'bg-[#40E0D0] text-white rounded-br-sm'
                          : isDark ? 'bg-gray-700 text-gray-100 rounded-bl-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                      }`}>
                        {msg.content}
                      </div>
                    )}
                    <p className={`text-xs mt-1 ${textSecondary} ${isMine ? 'text-right' : 'text-left'}`}>
                      {formatMessageTime(msg.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {renderInput(sendDm)}
      </div>
    );
  };

  const renderGroupChat = () => {
    if (!selectedGroup) return null;

    const nonMembersFiltered = nonMembers.filter((c) =>
      addMemberSearch === '' ||
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(addMemberSearch.toLowerCase())
    );

    return (
      <div className="flex flex-col h-full">
        <div className={`flex items-center gap-2 px-3 py-3 border-b ${border} ${headerBg}`}>
          <button onClick={goBack} className={`p-1 rounded-md transition-colors ${hoverBg} ${textSecondary}`}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#40E0D0] to-[#2ab5a8] flex items-center justify-center flex-shrink-0">
            <Hash className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold truncate ${textPrimary}`}>{selectedGroup.name}</p>
            <p className={`text-xs ${textSecondary}`}>{groupMembers.length} member{groupMembers.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => { setShowAddMember((v) => !v); setAddMemberSearch(''); setAddMemberError(null); }}
            className={`p-1.5 rounded-md transition-colors ${hoverBg} text-[#40E0D0]`}
            title="Add member"
          >
            <UserPlus className="w-4 h-4" />
          </button>
          <button onClick={() => setOpen(false)} className={`p-1 rounded-md transition-colors ${hoverBg} ${textSecondary}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {showAddMember && (
          <div className={`border-b ${border} p-3 space-y-2`}>
            <p className={`text-xs font-medium ${textSecondary}`}>Add a connection to this group</p>
            <input
              type="text"
              value={addMemberSearch}
              onChange={(e) => { setAddMemberSearch(e.target.value); setAddMemberError(null); }}
              placeholder="Search connections..."
              className={`w-full px-2.5 py-1.5 rounded-lg border text-xs outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputBg} ${textPrimary}`}
            />
            {addMemberError && (
              <p className="text-xs text-red-500 font-medium">{addMemberError}</p>
            )}
            <div className="max-h-36 overflow-y-auto space-y-1">
              {nonMembersFiltered.length === 0 ? (
                <p className={`text-xs text-center py-2 ${textSecondary}`}>
                  {connections.length === 0 ? 'No connections yet.' : 'All connections are already in this group.'}
                </p>
              ) : (
                nonMembersFiltered.map((c) => (
                  <button
                    key={c.userId}
                    onClick={() => addMemberToGroup(c.userId)}
                    disabled={addingMemberId === c.userId}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors text-left ${hoverBg} disabled:opacity-50`}
                  >
                    <Avatar photoUrl={c.photoUrl} firstName={c.firstName} lastName={c.lastName} size={7} />
                    <span className={`flex-1 text-xs truncate ${textPrimary}`}>{c.firstName} {c.lastName}</span>
                    {addingMemberId === c.userId
                      ? <div className="w-3.5 h-3.5 animate-spin rounded-full border-2 border-[#40E0D0] border-t-transparent flex-shrink-0" />
                      : <Plus className="w-3.5 h-3.5 text-[#40E0D0] flex-shrink-0" />
                    }
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <div className="flex justify-center pt-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#40E0D0]"></div>
            </div>
          ) : groupMessages.length === 0 ? (
            <div className={`flex flex-col items-center justify-center h-full gap-2 text-center ${textSecondary}`}>
              <Hash className="w-8 h-8 opacity-30" />
              <p className="text-xs">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            groupMessages.map((msg, idx) => {
              const isMine = msg.senderId === user?.id;
              const prevMsg = idx > 0 ? groupMessages[idx - 1] : null;
              const isNewBlock = !prevMsg || prevMsg.senderId !== msg.senderId;
              const displayName = isMine ? 'You' : msg.senderName;
              return (
                <div key={msg.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
                  {isNewBlock && (
                    <p className={`text-xs font-semibold mb-1 ${isMine ? 'mr-1 text-right' : 'ml-1 text-left'} ${textSecondary}`}>
                      {displayName}
                    </p>
                  )}
                  <div className="max-w-[78%]">
                    {msg.imageUrl && (
                      <div className={`mb-1 rounded-2xl overflow-hidden ${isMine ? 'rounded-br-sm' : 'rounded-bl-sm'}`}>
                        <img
                          src={msg.imageUrl}
                          alt="shared image"
                          className="max-w-full max-h-48 object-contain w-full cursor-pointer"
                          onClick={() => window.open(msg.imageUrl!, '_blank')}
                        />
                      </div>
                    )}
                    {msg.content && (
                      <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                        isMine
                          ? 'bg-[#40E0D0] text-white rounded-br-sm'
                          : isDark ? 'bg-gray-700 text-gray-100 rounded-bl-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                      }`}>
                        {msg.content}
                      </div>
                    )}
                    <p className={`text-xs mt-1 ${textSecondary} ${isMine ? 'text-right' : 'text-left'}`}>
                      {formatMessageTime(msg.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {renderInput(sendGroupMessage)}
      </div>
    );
  };

  const renderInput = (onSend: () => void) => (
    <div className={`px-3 py-3 border-t ${border}`}>
      {imagePreview && (
        <div className="mb-2 relative inline-block">
          <img src={imagePreview} alt="preview" className="max-h-24 rounded-xl object-contain border border-gray-300" />
          <button
            onClick={clearImage}
            className="absolute -top-1.5 -right-1.5 bg-white rounded-full shadow text-gray-500 hover:text-red-500 transition-colors"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className={`flex items-end gap-2 rounded-xl border px-3 py-2 transition-colors ${inputBg}`}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageSelect}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className={`p-1 rounded-md transition-colors flex-shrink-0 ${textSecondary} hover:text-[#40E0D0]`}
          title="Attach image"
          type="button"
        >
          <ImagePlus className="w-4 h-4" />
        </button>
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write a message…"
          rows={1}
          className={`flex-1 resize-none bg-transparent text-sm outline-none max-h-28 ${textPrimary} placeholder:text-gray-400`}
          style={{ lineHeight: '1.5' }}
        />
        <button
          onClick={onSend}
          disabled={!canSend}
          className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
            canSend
              ? 'bg-[#40E0D0] hover:bg-[#35c5b5] text-white'
              : isDark ? 'bg-gray-700 text-gray-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {uploadingImage ? (
            <div className="w-3.5 h-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
      <p className={`text-xs mt-1.5 ${textSecondary}`}>Enter to send · Shift+Enter for new line</p>
    </div>
  );

  const renderList = () => (
    <>
      <div className={`flex items-center justify-between px-4 py-3 border-b ${border} ${headerBg}`}>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-[#40E0D0]" />
          <span className={`font-semibold text-sm ${textPrimary}`}>Messages</span>
          {totalDmUnread > 0 && (
            <span className="bg-[#40E0D0] text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
              {totalDmUnread}
            </span>
          )}
        </div>
        <button onClick={() => setOpen(false)} className={`p-1 rounded-md transition-colors ${hoverBg} ${textSecondary}`}>
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className={`flex border-b ${border}`}>
        {(['dms', 'groups'] as ActiveTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setSearch(''); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-[#40E0D0] text-[#40E0D0]'
                : `${textSecondary} ${hoverBg}`
            }`}
          >
            {tab === 'dms' ? <MessageCircle className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
            {tab === 'dms' ? 'Direct' : 'Groups'}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className={`px-3 py-2 border-b ${border}`}>
        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <Search className={`w-3.5 h-3.5 flex-shrink-0 ${textSecondary}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={activeTab === 'dms' ? 'Search connections…' : 'Search groups…'}
            className={`flex-1 bg-transparent text-xs outline-none ${textPrimary} placeholder:text-gray-400`}
          />
        </div>
      </div>

      {activeTab === 'dms' ? renderDmList() : renderGroupList()}
    </>
  );

  // ─── Main render ─────────────────────────────────────────────────────────────

  return (
    <>
      {/* Toggle tab */}
      <div
        className={`fixed z-40 flex items-center transition-all duration-300 ease-in-out ${
          open ? 'right-[360px]' : 'right-0'
        }`}
        style={{ top: '50%', transform: 'translateY(-50%)', marginTop: '48px' }}
      >
        <button
          onClick={() => { setOpen((v) => !v); if (!open) { setView('list'); setSelectedDm(null); setSelectedGroup(null); } }}
          className={`relative flex items-center justify-center w-8 h-20 rounded-l-xl shadow-xl border border-r-0 transition-colors ${
            isDark
              ? 'bg-gray-800 border-gray-700 hover:bg-gray-700 text-gray-300'
              : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-600'
          }`}
          title={open ? 'Hide chat' : 'Open messages'}
        >
          {!open && totalDmUnread > 0 && (
            <span className="absolute -top-1.5 -left-1.5 bg-[#40E0D0] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {totalDmUnread > 9 ? '9+' : totalDmUnread}
            </span>
          )}
          {open ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Panel */}
      <div
        className={`fixed right-0 bottom-0 z-30 flex flex-col border-l shadow-2xl transition-all duration-300 ease-in-out ${bg} ${border} ${
          open ? 'w-[360px]' : 'w-0 overflow-hidden'
        }`}
        style={{ top: '96px', maxWidth: '100vw' }}
      >
        {open && (
          view === 'list' ? renderList()
          : view === 'dm' ? renderDmChat()
          : view === 'group' ? renderGroupChat()
          : renderNewGroup()
        )}
      </div>
    </>
  );
}
